import { useCallback, useEffect, useMemo, useState } from 'react';
import '@/App.css';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Toaster } from '@/components/ui/sonner';
import HeaderBar from '@/components/HeaderBar';
import StatsStrip from '@/components/StatsStrip';
import FilterBar from '@/components/FilterBar';
import ScorecardGrid from '@/components/ScorecardGrid';
import SubscribeForm from '@/components/SubscribeForm';
import ConstructorsTable from '@/components/ConstructorsTable';
import SectorProfileCards from '@/components/SectorProfileCards';
import F1ConstructorProfiles from '@/components/F1ConstructorProfiles';
import Top10Podium from '@/components/Top10Podium';
import CompanyDetailModal from '@/components/CompanyDetailModal';
import CompareModal from '@/components/CompareModal';
import CompletedRaceDashboard from '@/components/CompletedRaceDashboard';
import HeroBanner from '@/components/HeroBanner';
import AdminPanel from '@/components/AdminPanel';
import { fetchFilters, fetchStats } from '@/lib/api';

const DEFAULT_FILTERS = {
  country: '',
  sector: '',
  geography: '',
  market_cap: '',
  validation_status: '',
  sort_by: 'overall_score',
  sort_order: 'desc',
};

function App() {
  const isAdminRoute = typeof window !== 'undefined' && window.location.pathname === '/admin';
  if (isAdminRoute) return <AdminPanel />;

  return <Dashboard />;
}

function Dashboard() {
  const [activeTab, setActiveTab] = useState('completed');
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState(DEFAULT_FILTERS);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [isEmbed, setIsEmbed] = useState(false);
  const [isPageEmbed, setIsPageEmbed] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const embedVal = params.get('embed');
      setIsEmbed(embedVal === '1');
      setIsPageEmbed(embedVal === 'page');
    }
  }, []);

  useEffect(() => {
    if (isEmbed && !isPageEmbed) return;
    const interval = window.setInterval(() => {
      setRefreshTick((previous) => previous + 1);
    }, 600000);
    return () => window.clearInterval(interval);
  }, [isEmbed, isPageEmbed]);

  useEffect(() => {
    fetchFilters({ completed_only: true }).then(setFilters).catch(console.error);
  }, [refreshTick]);

  useEffect(() => {
    const params = { completed_only: true };
    if (selectedFilters.country) params.country = selectedFilters.country;
    fetchStats(params)
      .then(setStats)
      .catch(console.error);
  }, [selectedFilters.country, refreshTick]);

  const handleCompanyClick = useCallback((company) => {
    setSelectedCompany(company);
  }, []);

  const handleCompareToggle = useCallback((companyId) => {
    setCompareIds((prev) => {
      if (prev.includes(companyId)) {
        return prev.filter((id) => id !== companyId);
      }
      if (prev.length >= 3) return prev;
      return [...prev, companyId];
    });
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    setSelectedFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedFilters(DEFAULT_FILTERS);
  }, []);

  const handleCountrySelection = useCallback((country) => {
    setSelectedFilters((prev) => ({ ...prev, country }));
    setActiveTab('scorecard');
  }, []);

  const selectedCountryLabel = selectedFilters.country || 'All GP Countries';
  const compareReady = compareIds.length >= 2;

  const tabs = useMemo(
    () => [
      { value: 'completed', label: 'Completed GPs', testId: 'nav-tab-completed-gps' },
      { value: 'scorecard', label: 'F1 Scorecard', testId: 'nav-tab-scorecard' },
      { value: 'constructors', label: 'Constructors', testId: 'nav-tab-constructors' },
      { value: 'top10', label: 'Top 10', testId: 'nav-tab-top-10' },
    ],
    []
  );

  return (
    <div className={`app-wrapper ${isEmbed ? 'embed-mode' : ''} ${isPageEmbed ? 'page-embed-mode' : ''}`}>
      <Toaster />
      <div className="app-container">
        <HeaderBar
          compareCount={compareIds.length}
          compareReady={compareReady}
          onOpenCompare={() => setShowCompare(true)}
          selectedCountry={selectedCountryLabel}
          countryCount={filters?.countries?.length || 0}
          isEmbed={isEmbed}
          isPageEmbed={isPageEmbed}
        />

        <div className="relative z-10">
          <HeroBanner refreshTick={refreshTick} />

          {(!isEmbed || isPageEmbed) && activeTab !== 'completed' ? <StatsStrip stats={stats} selectedCountry={selectedFilters.country} /> : null}

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
            <div className="overflow-x-auto pb-2">
              <TabsList className="inline-flex min-w-max rounded-2xl border border-white/10 bg-white/5 p-1 shadow-[0_10px_30px_rgba(0,0,0,0.3)]" data-testid="view-mode-tabs">
                {tabs.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    data-testid={tab.testId}
                    className="rounded-xl px-4 py-2.5 text-sm font-medium text-white/60 transition-colors duration-150 data-[state=active]:bg-white/10 data-[state=active]:text-white data-[state=active]:shadow-[inset_0_-2px_0_#2A9D8F]"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <TabsContent value="completed" className="mt-6">
              <CompletedRaceDashboard
                isEmbed={isEmbed}
                onSelectCountry={handleCountrySelection}
                onCompanyClick={handleCompanyClick}
                compareIds={compareIds}
                onCompareToggle={handleCompareToggle}
                refreshTick={refreshTick}
              />
            </TabsContent>

            <TabsContent value="scorecard" className="mt-6 space-y-5">
              <FilterBar
                filters={filters}
                selectedFilters={selectedFilters}
                onFilterChange={handleFilterChange}
                onClear={clearFilters}
              />
              <ScorecardGrid
                selectedFilters={selectedFilters}
                onCompanyClick={handleCompanyClick}
                compareIds={compareIds}
                onCompareToggle={handleCompareToggle}
                onClearFilters={clearFilters}
                refreshTick={refreshTick}
              />
            </TabsContent>

            <TabsContent value="constructors" className="mt-6 space-y-8">
              <F1ConstructorProfiles refreshTick={refreshTick} />
              <ConstructorsTable onCompanyClick={handleCompanyClick} refreshTick={refreshTick} />
              {(!isEmbed || isPageEmbed) ? <SectorProfileCards /> : null}
            </TabsContent>

            <TabsContent value="top10" className="mt-6">
              <Top10Podium
                onCompanyClick={handleCompanyClick}
                compareIds={compareIds}
                onCompareToggle={handleCompareToggle}
                selectedCountry={selectedFilters.country}
                refreshTick={refreshTick}
              />
            </TabsContent>
          </Tabs>
        </div>

        {(!isEmbed || isPageEmbed) ? (
          <footer className="mt-12 border-t border-white/10 pb-20 pt-6">
            <div className="mb-8">
              <SubscribeForm />
            </div>
            <p className="max-w-4xl text-xs text-white/45" data-testid="disclaimer-text">
              Country leaderboards prioritize in-country coverage first and transparently label regional fallback where domestic public-company depth is limited.
              F1 session results are sourced from public motorsport timing/results feeds and may be delayed.
            </p>
            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="space-y-1.5">
                <div className="flex items-center gap-3" data-testid="footer-brand-block">
                  <span className="text-sm font-bold tracking-tight text-white/50">AA Impact Inc</span>
                  <span className="text-white/10">·</span>
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#38bdf8]/40">A2 Intelligence</span>
                </div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-white/20">Precision Sustainability Intelligence</p>
                <p className="text-xs text-white/30">&copy; 2026 AA Impact Inc. All rights reserved.</p>
              </div>
              <p className="text-xs text-white/25">{selectedCountryLabel} &middot; Formula 1 market coverage</p>
            </div>
          </footer>
        ) : null}
      </div>

      <CompanyDetailModal
        company={selectedCompany}
        onClose={() => setSelectedCompany(null)}
        onCompare={(id) => {
          handleCompareToggle(id);
          setSelectedCompany(null);
        }}
      />

      <CompareModal
        open={showCompare}
        onClose={() => setShowCompare(false)}
        compareIds={compareIds}
        onRemove={(id) => handleCompareToggle(id)}
      />
    </div>
  );
}

export default App;
